import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Sparkles, Send, Mic, Volume2, Loader2, LogOut, WifiOff, Wifi, CloudOff,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { loadBank, offlineAnswer, cachedAudioUrl, OFFLINE_FALLBACK } from '../lib/offline';

const STARTERS = [
  'Ton pire défaut, avoue ?',
  "J'hésite entre deux jobs, tu ferais quoi ?",
  "La pizza à l'ananas, verdict ?",
  'Un conseil pour négocier mon loyer ?',
];

const PRENOM = 'Olivier';

export default function ChatScreen({ setScreen, logoutGuest, simOffline, setSimOffline, voiceEnabled, adminAuthed }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [netDown, setNetDown] = useState(typeof navigator !== 'undefined' && !navigator.onLine);
  const [voiceBusy, setVoiceBusy] = useState(null);
  const scrollRef = useRef(null);
  const audioRef = useRef(null);
  const isOffline = simOffline || netDown;

  useEffect(() => {
    const on = () => setNetDown(false);
    const off = () => setNetDown(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  const serveOffline = useCallback((switched) => (text) => {
    const hit = offlineAnswer(loadBank(), text);
    const content = hit ? hit.answer : OFFLINE_FALLBACK;
    setBusy(true);
    setTimeout(() => {
      setMessages((m) => [...m, {
        role: 'assistant', content, offline: true, audioUrl: hit?.audio_url || null, switched,
      }]);
      setBusy(false);
    }, 650);
  }, []);

  const send = useCallback(async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || busy) return;
    setErr('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setInput('');

    if (isOffline) {
      serveOffline(false)(text);
      return;
    }

    setBusy(true);
    try {
      const data = await api.post('/api/chat', { message: text });
      setMessages((m) => [...m, { role: 'assistant', content: data.reply, messageId: data.message_id }]);
      setBusy(false);
    } catch (e) {
      setBusy(false);
      if (e instanceof ApiError) {
        if (e.status === 401) {
          logoutGuest();
          setScreen('gate');
          return;
        }
        setErr(e.message);
      } else {
        // Échec réseau en cours de session → bascule automatique sur la banque locale
        setNetDown(true);
        serveOffline(true)(text);
      }
    }
  }, [input, busy, isOffline, serveOffline, logoutGuest, setScreen]);

  const playVoice = async (msg, index) => {
    if (voiceBusy !== null) return;
    setVoiceBusy(index);
    try {
      let url = null;
      if (msg.offline) {
        url = (await cachedAudioUrl(msg.audioUrl)) || msg.audioUrl;
      } else if (msg.messageId) {
        const data = await api.post('/api/tts', { message_id: msg.messageId });
        url = data.audio_url;
      }
      if (url) {
        audioRef.current?.pause();
        audioRef.current = new Audio(url);
        await audioRef.current.play();
      }
    } catch (e) {
      setErr(e.message || 'La voix est momentanément indisponible.');
    } finally {
      setVoiceBusy(null);
    }
  };

  return (
    <div className="page chat-page">
      <div className="chat-shell">
        <div className="chat-top">
          <div className="chat-ava"><img src="/logo.svg" alt="" /></div>
          <div className="chat-top-name">
            {PRENOM}
            {isOffline ? (
              <span className="chat-top-sub off"><WifiOff size={11} /> mode hors ligne</span>
            ) : (
              <span className="chat-top-sub"><span className="live-dot" /> disponible — pour une fois</span>
            )}
          </div>
          <button
            className={`sim-toggle${simOffline ? ' on' : ''}`}
            onClick={() => setSimOffline(!simOffline)}
            title="Démo : simuler une coupure réseau"
          >
            {simOffline ? <WifiOff size={14} /> : <Wifi size={14} />}
          </button>
          <button
            className="chat-x"
            onClick={() => { logoutGuest(); setScreen(adminAuthed ? 'admin' : 'landing'); }}
            aria-label={adminAuthed ? "Revenir à l'administration" : 'Quitter'}
          >
            <LogOut size={15} />
          </button>
        </div>

        {isOffline && (
          <div className="off-banner">
            <CloudOff size={14} /> Connexion coupée — les réponses sont servies depuis la banque locale de l'appareil.
          </div>
        )}

        <div className="chat-scroll" ref={scrollRef}>
          {messages.length === 0 && (
            <div className="chat-empty">
              <div className="chat-empty-ava"><Sparkles size={20} /></div>
              <p>Posez-lui ce que vous voulez. Il a un avis sur tout — surtout quand on ne lui demande pas.</p>
              <div className="starters">
                {STARTERS.map((s) => (
                  <button key={s} className="starter" onClick={() => send(s)} disabled={busy}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`msg ${m.role}`}>
              {m.role === 'assistant' && <div className="msg-ava"><img src="/logo.svg" alt="" /></div>}
              <div className="bubble">
                {m.switched && <span className="switch-note">Réseau perdu — bascule automatique hors ligne</span>}
                {m.content}
                {m.role === 'assistant' && (
                  m.offline ? (
                    m.audioUrl ? (
                      <button className="voice-btn" onClick={() => playVoice(m, i)} disabled={voiceBusy !== null}>
                        {voiceBusy === i ? <Loader2 size={11} className="spin" /> : <Volume2 size={11} />} audio local
                      </button>
                    ) : (
                      <span className="off-chip"><WifiOff size={11} /> hors ligne</span>
                    )
                  ) : (
                    voiceEnabled ? (
                      <button className="voice-btn" onClick={() => playVoice(m, i)} disabled={voiceBusy !== null}>
                        {voiceBusy === i ? <Loader2 size={11} className="spin" /> : <Volume2 size={13} />} voix
                      </button>
                    ) : (
                      <button className="voice-btn" title="Voix bientôt disponible" disabled><Volume2 size={13} /> voix</button>
                    )
                  )
                )}
              </div>
            </div>
          ))}
          {busy && (
            <div className="msg assistant">
              <div className="msg-ava"><img src="/logo.svg" alt="" /></div>
              <div className="bubble typing"><i /><i /><i /></div>
            </div>
          )}
        </div>

        {err && <div className="chat-err">{err}</div>}

        <div className="chat-input">
          <button className="mic-btn" title="Voix bientôt disponible" disabled><Mic size={17} /></button>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder="Votre question…"
            disabled={busy}
          />
          <button className="send-btn" onClick={() => send()} disabled={busy || !input.trim()}>
            {busy ? <Loader2 size={17} className="spin" /> : <Send size={17} />}
          </button>
        </div>
      </div>
    </div>
  );
}
